/**
 * Terms and Conditions Page
 */
import { useEffect } from 'react';
import { FileText } from 'lucide-react';
import SEOHead from '../components/common/SEOHead';
import PublicHeader from '../components/common/PublicHeader';

const TermosPage = () => {
  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      <SEOHead
        title="Termos e Condições — IMOCODE"
        description="Termos de utilização da plataforma IMOCODE para agentes imobiliários. Leia os termos legais que regem o uso da aplicação, QR Codes e serviços associados."
        path="/termos"
      />
      <PublicHeader />

      {/* Content */}
      <main className="container mx-auto px-6 py-12 pt-28 max-w-4xl">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 md:p-12">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
              <FileText className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Termos e Condições</h1>
              <p className="text-gray-500">IMOCODE</p>
            </div>
          </div>

          <p className="text-sm text-gray-500 mb-8">Última atualização: Março 2025</p>

          <div className="prose prose-gray max-w-none">
            <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-4">1. Identificação do serviço</h2>
            <p className="text-gray-600 mb-4">O serviço IMOCODE é uma plataforma digital disponibilizada pela:</p>
            <p className="text-gray-800 font-medium mb-4">Cardume Pioneiro – Serviços Digitais Lda.</p>
            <p className="text-gray-600 mb-4">doravante designada por IMOCODE ou Prestador do Serviço.</p>
            <p className="text-gray-600 mb-4">O serviço permite a criação e gestão de QR Codes dinâmicos associados a imóveis, facilitando a partilha de informação imobiliária, captação de contactos e gestão de leads.</p>
            <p className="text-gray-600 mb-4">Para qualquer questão relacionada com o serviço, o utilizador poderá contactar:</p>
            <p className="text-gray-600 mb-4">📧 <a href="mailto:suporte@imocode.app" className="text-blue-600 hover:underline">suporte@imocode.app</a></p>

            <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-4">2. Aceitação dos termos</h2>
            <p className="text-gray-600 mb-4">Ao utilizar o website ou os serviços da plataforma IMOCODE, o utilizador declara que:</p>
            <ul className="list-disc pl-6 text-gray-600 mb-4 space-y-1">
              <li>leu</li>
              <li>compreendeu</li>
              <li>aceita</li>
            </ul>
            <p className="text-gray-600 mb-4">os presentes Termos e Condições.</p>
            <p className="text-gray-600 mb-4">Caso não concorde com estes termos, deverá cessar a utilização da plataforma.</p>

            <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-4">3. Descrição do serviço</h2>
            <p className="text-gray-600 mb-4">A plataforma IMOCODE permite aos utilizadores:</p>
            <ul className="list-disc pl-6 text-gray-600 mb-4 space-y-1">
              <li>criar e gerir QR Codes dinâmicos</li>
              <li>associar QR Codes a imóveis ou páginas externas</li>
              <li>captar leads de potenciais interessados</li>
              <li>gerir pedidos de contacto ou visitas</li>
              <li>aceder a estatísticas de visualização</li>
              <li>partilhar anúncios imobiliários</li>
            </ul>
            <p className="text-gray-600 mb-4">O serviço pode incluir funcionalidades gratuitas e pagas.</p>

            <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-4">4. Criação de conta</h2>
            <p className="text-gray-600 mb-4">Para utilizar determinadas funcionalidades, o utilizador deverá criar uma conta na plataforma.</p>
            <p className="text-gray-600 mb-4">O utilizador compromete-se a:</p>
            <ul className="list-disc pl-6 text-gray-600 mb-4 space-y-1">
              <li>fornecer informação verdadeira</li>
              <li>manter os dados atualizados</li>
              <li>garantir a confidencialidade das credenciais de acesso</li>
            </ul>
            <p className="text-gray-600 mb-4">O utilizador é responsável por todas as atividades realizadas através da sua conta.</p>

            <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-4">5. Planos e pagamentos</h2>
            <p className="text-gray-600 mb-4">O IMOCODE disponibiliza diferentes planos de utilização, que podem incluir:</p>
            <ul className="list-disc pl-6 text-gray-600 mb-4 space-y-1">
              <li>plano gratuito</li>
              <li>planos pagos por subscrição</li>
              <li>aquisição de funcionalidades adicionais</li>
            </ul>
            <p className="text-gray-600 mb-4">Os preços e condições são apresentados na plataforma.</p>
            <p className="text-gray-600 mb-4">Os pagamentos podem ser efetuados através de meios eletrónicos disponibilizados no website.</p>
            <p className="text-gray-600 mb-4">As subscrições podem ser renovadas automaticamente, salvo cancelamento pelo utilizador.</p>

            <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-4">6. Utilização adequada do serviço</h2>
            <p className="text-gray-600 mb-4">O utilizador compromete-se a não utilizar o serviço para:</p>
            <ul className="list-disc pl-6 text-gray-600 mb-4 space-y-1">
              <li>atividades ilegais</li>
              <li>divulgação de conteúdos fraudulentos</li>
              <li>spam ou recolha abusiva de dados</li>
              <li>qualquer atividade que viole legislação aplicável</li>
            </ul>
            <p className="text-gray-600 mb-4">O IMOCODE reserva-se o direito de suspender contas que violem estas regras.</p>

            <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-4">7. Conteúdos inseridos pelos utilizadores</h2>
            <p className="text-gray-600 mb-4">Os utilizadores são responsáveis pelos conteúdos publicados na plataforma, incluindo:</p>
            <ul className="list-disc pl-6 text-gray-600 mb-4 space-y-1">
              <li>descrições de imóveis</li>
              <li>imagens</li>
              <li>informações comerciais</li>
              <li>links externos</li>
            </ul>
            <p className="text-gray-600 mb-4">O IMOCODE não se responsabiliza pela veracidade ou legalidade destes conteúdos.</p>

            <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-4">8. Disponibilidade do serviço</h2>
            <p className="text-gray-600 mb-4">O IMOCODE envidará esforços para garantir a disponibilidade da plataforma.</p>
            <p className="text-gray-600 mb-4">Contudo, não garante funcionamento ininterrupto, podendo ocorrer:</p>
            <ul className="list-disc pl-6 text-gray-600 mb-4 space-y-1">
              <li>manutenções técnicas</li>
              <li>atualizações</li>
              <li>interrupções temporárias</li>
            </ul>

            <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-4">9. Limitação de responsabilidade</h2>
            <p className="text-gray-600 mb-4">O IMOCODE não será responsável por:</p>
            <ul className="list-disc pl-6 text-gray-600 mb-4 space-y-1">
              <li>perdas indiretas</li>
              <li>danos comerciais</li>
              <li>decisões tomadas com base nas informações apresentadas na plataforma</li>
            </ul>
            <p className="text-gray-600 mb-4">O serviço é disponibilizado "tal como está".</p>

            <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-4">10. Propriedade intelectual</h2>
            <p className="text-gray-600 mb-4">Todo o conteúdo da plataforma, incluindo:</p>
            <ul className="list-disc pl-6 text-gray-600 mb-4 space-y-1">
              <li>design</li>
              <li>software</li>
              <li>marca</li>
              <li>logotipo</li>
              <li>funcionalidades</li>
            </ul>
            <p className="text-gray-600 mb-4">é propriedade da Cardume Pioneiro – Serviços Digitais Lda.</p>
            <p className="text-gray-600 mb-4">É proibida a reprodução ou utilização sem autorização.</p>

            <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-4">11. Cancelamento da conta</h2>
            <p className="text-gray-600 mb-4">O utilizador pode cancelar a conta a qualquer momento.</p>
            <p className="text-gray-600 mb-4">O IMOCODE poderá suspender ou encerrar contas em caso de:</p>
            <ul className="list-disc pl-6 text-gray-600 mb-4 space-y-1">
              <li>violação dos termos</li>
              <li>uso indevido do serviço</li>
              <li>atividades fraudulentas</li>
            </ul>

            <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-4">12. Alterações aos termos</h2>
            <p className="text-gray-600 mb-4">O IMOCODE pode alterar os presentes termos a qualquer momento.</p>
            <p className="text-gray-600 mb-4">As alterações serão publicadas na plataforma.</p>

            <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-4">13. Lei aplicável</h2>
            <p className="text-gray-600 mb-4">Os presentes termos regem-se pela legislação portuguesa.</p>
            <p className="text-gray-600 mb-4">Em caso de litígio, serão competentes os tribunais portugueses.</p>
          </div>
        </div>
      </main>
    </div>
  );
};

export default TermosPage;
